import { levelColor, fmtTime } from "./format";

export interface SeriesPoint { ts: number; premium: number; skhy: number }

/**
 * A viewer's visible window, as category indices from the previous render.
 * Indices survive a refresh because a refresh only ever appends categories.
 * `endIdx: null` means they were parked on the right edge — keep them there so
 * the view goes on following live data.
 */
export interface ZoomWindow { startIdx: number; endIdx: number | null }

export function buildOption(
  series: SeriesPoint[],
  krwSeries: { ts: number; close: number }[],
  band: { ts: number; mean: number; upper: number; lower: number }[],
  signals: { ts: number; premium: number; level: string }[],
  keep?: ZoomWindow,
) {
  // Trading-time (category) x-axis: only real data timestamps become categories,
  // so non-trading spans (weekends/holidays/closures) collapse — lines connect
  // straight across them with no gap, showing trading days only.
  const catSet = new Set<number>();
  for (const s of series) catSet.add(s.ts);
  for (const k of krwSeries) catSet.add(k.ts);
  const cats = [...catSet].sort((a, b) => a - b);
  const idx = new Map<number, number>(cats.map((ts, i) => [ts, i]));
  const N = cats.length;
  const align = <T,>(pts: T[], getTs: (p: T) => number, getVal: (p: T) => number): (number | null)[] => {
    const a: (number | null)[] = new Array(N).fill(null);
    for (const p of pts) {
      const i = idx.get(getTs(p));
      if (i != null) a[i] = getVal(p);
    }
    return a;
  };
  const label = (ts: number) => fmtTime(ts).slice(0, 11); // "MM-DD HH:MM"

  // Default the visible window to the most recent ~1 day (full history stays
  // loaded — drag the slider to see more). On the 60s refresh `keep` carries
  // the viewer's own window across instead, so reloading doesn't yank them back.
  const pct = (i: number) => (N > 1 ? Math.min(100, Math.max(0, (i / (N - 1)) * 100)) : 0);
  const lastTs = N ? cats[N - 1] : 0;
  let dayIdx = cats.findIndex((ts) => ts >= lastTs - 86400);
  if (dayIdx < 0) dayIdx = 0;
  const startPct = pct(keep ? keep.startIdx : dayIdx);
  const endPct = keep && keep.endIdx != null ? pct(keep.endIdx) : 100;

  return {
    backgroundColor: "#FAFAFA",
    textStyle: { fontFamily: "Space Mono, monospace" },
    legend: { data: ["premium", "SKHY", "000660"], top: 6, itemGap: 18, textStyle: { fontFamily: "Space Mono, monospace", color: "#0A0A0A" } },
    grid: { left: 56, right: 76, top: 40, bottom: 48, borderColor: "#0A0A0A" },
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "line", lineStyle: { color: "#0A0A0A", width: 1, type: "dashed" } },
      backgroundColor: "#FDFBF7", borderColor: "#0A0A0A", borderWidth: 2, padding: 8,
      textStyle: { color: "#0A0A0A", fontFamily: "Space Mono, monospace", fontSize: 12 },
      formatter: (ps: any[]) => {
        const by: Record<string, number> = {};
        for (const p of ps) if (p.value != null) by[p.seriesName] = p.value;
        const when = ps[0]?.axisValueLabel ?? "";
        const lines: string[] = [];
        if (by.premium != null) lines.push(`<b>溢价 ${by.premium >= 0 ? "+" : ""}${by.premium.toFixed(2)}%</b>`);
        if (by.SKHY != null) lines.push(`SKHY $${by.SKHY.toFixed(2)}`);
        if (by["000660"] != null) lines.push(`000660 ₩${Math.round(by["000660"]).toLocaleString("en-US")}`);
        return `${when}<br/>${lines.join("<br/>")}`;
      },
    },
    xAxis: {
      type: "category",
      data: cats,
      boundaryGap: false,
      axisLine: { lineStyle: { color: "#0A0A0A", width: 2 } },
      axisLabel: { formatter: (v: any) => label(Number(v)) },
    },
    yAxis: [
      { type: "value", name: "溢价%", position: "left", axisLine: { show: true, lineStyle: { color: "#0A0A0A", width: 2 } }, splitLine: { lineStyle: { color: "#00000010" } } },
      { type: "value", name: "SKHY $", position: "right", scale: true, axisLine: { show: true, lineStyle: { color: "#2979FF", width: 2 } }, splitLine: { show: false } },
      { type: "value", name: "000660 ₩", position: "right", offset: 56, scale: true, axisLine: { show: true, lineStyle: { color: "#00C853", width: 2 } }, splitLine: { show: false }, axisLabel: { formatter: (v: number) => (v / 1000).toFixed(0) + "k" } },
    ],
    dataZoom: [
      { type: "inside", start: startPct, end: endPct, zoomOnMouseWheel: false, moveOnMouseWheel: false, moveOnMouseMove: false },
      { type: "slider", borderColor: "#0A0A0A", start: startPct, end: endPct },
    ],
    series: [
      { name: "lower", type: "line", stack: "band", yAxisIndex: 0, showSymbol: false, silent: true, lineStyle: { opacity: 0 }, areaStyle: { opacity: 0 }, tooltip: { show: false }, data: align(band, (b) => b.ts, (b) => b.lower) },
      { name: "band", type: "line", stack: "band", yAxisIndex: 0, showSymbol: false, silent: true, lineStyle: { opacity: 0 }, areaStyle: { color: "#EAB30822" }, tooltip: { show: false }, data: align(band, (b) => b.ts, (b) => b.upper - b.lower) },
      { name: "premium", type: "line", yAxisIndex: 0, showSymbol: false, connectNulls: true, sampling: "lttb", lineStyle: { color: "#0A0A0A", width: 2 }, data: align(series, (s) => s.ts, (s) => s.premium) },
      { name: "SKHY", type: "line", yAxisIndex: 1, showSymbol: false, connectNulls: true, sampling: "lttb", lineStyle: { color: "#2979FF", width: 1 }, data: align(series, (s) => s.ts, (s) => s.skhy) },
      { name: "000660", type: "line", yAxisIndex: 2, showSymbol: false, connectNulls: true, sampling: "lttb", lineStyle: { color: "#00C853", width: 1 }, data: align(krwSeries, (k) => k.ts, (k) => k.close) },
      {
        name: "signals", type: "scatter", yAxisIndex: 0, symbolSize: 12, tooltip: { show: false },
        data: signals
          .map((s) => {
            const i = idx.get(s.ts);
            return i == null ? null : { value: [i, s.premium], itemStyle: { color: levelColor(s.level), borderColor: "#0A0A0A", borderWidth: 2 } };
          })
          .filter((x): x is { value: [number, number]; itemStyle: object } => x != null),
      },
    ],
  };
}
